import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { EntitlementStatus } from '../services/entitlement/types';
import { isStoreProEntitlement } from '../services/entitlement/entitlementHelpers';
import { FREE_SHEET_MAX } from '../logic/SheetLimitLogic';
import { BILLING_LEGAL_URLS, getBillingCopy } from '../i18n/billing';
import { openExternal } from '../utils/openExternal';
import {
  detectBillingPlatform,
  isIosInAppPurchasePlatform,
} from '../services/entitlement/platform/detect';
import { isNativeStorePurchasePlatform } from '../services/entitlement/platform/appStoreRouter';
import { isMacAppStorePurchasePlatform } from '../services/entitlement/platform/macAppStore';
type PaywallModalProps = {
  open: boolean;
  busy: boolean;
  sheetCount: number;
  entitlement: EntitlementStatus;
  errorMessage?: string;
  redeemCodeInput?: string;
  onClose: () => void;
  onPurchase: () => void;
  onRestore: () => void;
  onManage?: () => void;
  onRedeemCodeInputChange?: (value: string) => void;
  onRedeem?: () => void;
  onOpenWebRedeemIssue?: () => void;
  onOpenStoreProduct?: () => void;
  showStoreFallbackLink?: boolean;
};

function runTouchAction(e: React.TouchEvent, action: () => void): void {
  e.preventDefault();
  e.stopPropagation();
  action();
}

function isWebRedeemPlatform(): boolean {
  return detectBillingPlatform() === 'web' && !(window as { electron?: unknown }).electron;
}

function PaywallLegalLink({
  href,
  label,
  disabled,
}: {
  href: string;
  label: string;
  disabled: boolean;
}) {
  return (
    <a
      href={href}
      className="paywall-inline-link"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault();
        if (!disabled) openExternal(href);
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!disabled) openExternal(href);
      }}
    >
      {label}
    </a>
  );
}

export function PaywallModal({
  open,
  busy,
  sheetCount,
  entitlement,
  errorMessage,
  redeemCodeInput = '',
  onClose,
  onPurchase,
  onRestore,
  onManage,
  onRedeemCodeInputChange,
  onRedeem,
  onOpenWebRedeemIssue,
  onOpenStoreProduct,
  showStoreFallbackLink = false,
}: PaywallModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    root.classList.add('paywall-open');
    return () => root.classList.remove('paywall-open');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = overlayRef.current;
    if (!el) return;
    const blockScroll = (e: TouchEvent) => e.preventDefault();
    el.addEventListener('touchmove', blockScroll, { passive: false });
    return () => el.removeEventListener('touchmove', blockScroll);
  }, [open]);

  if (!open) return null;

  const t = getBillingCopy();
  const isWebRedeem = isWebRedeemPlatform();
  const showNativePurchase = !isWebRedeem;
  const isStorePurchase = isNativeStorePurchasePlatform();
  const isIosStore = isIosInAppPurchasePlatform();
  const isMacStore = isMacAppStorePurchasePlatform();
  const hasStorePrice = Boolean(entitlement.formattedPrice);
  const priceLabel = entitlement.formattedPrice ?? '';
  const isStoreProNative =
    showNativePurchase && isStoreProEntitlement(entitlement);
  const storeCatalogLoading =
    isIosStore && isStorePurchase && !isStoreProNative && !hasStorePrice && !busy;
  const storePurchaseProcessing = busy && isStorePurchase && !isWebRedeem && !isStoreProNative;
  const platformNote = isIosStore
    ? t.platformNoteIosNative
    : isMacStore
      ? t.platformNoteMacNative
      : t.platformNote;
  const processingNote = isIosStore || isMacStore
    ? t.storePurchaseProcessingAppStore
    : t.storePurchaseProcessing;
  const showRestore = isStorePurchase;
  const showManage =
    showNativePurchase &&
    isStoreProNative &&
    isStorePurchase &&
    onManage;
  const showIssueButton =
    showNativePurchase &&
    isStoreProNative &&
    onOpenWebRedeemIssue;
  const showIosSubscriptionLegal = isIosStore && isStorePurchase && !isWebRedeem;
  const subscriptionPriceLine = hasStorePrice
    ? t.subscriptionPriceLine(priceLabel)
    : t.subscriptionPriceFallback;

  const modal = (
    <div
      ref={overlayRef}
      className={`custom-prompt-overlay paywall-overlay${storePurchaseProcessing ? ' paywall-store-purchasing' : ''}`}
    >
      <div
        className="custom-prompt-dialog paywall-dialog"
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="paywall-title"
      >
        <div className="custom-prompt-header">
          <h3 id="paywall-title">
            {isWebRedeem ? t.webRedeemTitle : isStoreProNative ? t.proActive : t.paywallTitle}
          </h3>
        </div>
        <div className="custom-prompt-body">
          {isWebRedeem ? (
            <>
              <p className="custom-prompt-message">{t.webRedeemBody}</p>
              <p className="paywall-platform-note">{t.webRedeemStoreNote}</p>
              <input
                type="text"
                className="custom-prompt-input paywall-redeem-input"
                inputMode="numeric"
                lang="en"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t.webRedeemInputPlaceholder}
                value={redeemCodeInput}
                disabled={busy}
                onChange={(e) => onRedeemCodeInputChange?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (!busy && redeemCodeInput.trim()) onRedeem?.();
                }}
              />
              <p className="paywall-platform-note">{t.webRedeemInputHint}</p>
            </>
          ) : isStoreProNative ? (
            <>
              <p className="custom-prompt-message">{t.paywallProManageBody}</p>
              {!showIosSubscriptionLegal ? (
                <p className="paywall-platform-note">{platformNote}</p>
              ) : null}
            </>
          ) : (
            <>
              <p className="custom-prompt-message">{t.paywallBody(sheetCount, FREE_SHEET_MAX)}</p>
              {!showIosSubscriptionLegal ? (
                <p className="paywall-platform-note">{platformNote}</p>
              ) : null}
            </>
          )}
          {showIosSubscriptionLegal ? (
            <div className="paywall-subscription-legal">
              <p>
                <span className="paywall-subscription-legal-label">{t.subscriptionLegalTitle}</span>{' '}
                {t.subscriptionProductName}
              </p>
              <p>{t.subscriptionDuration}</p>
              <p>{subscriptionPriceLine}</p>
              <p>{t.subscriptionAutoRenew}</p>
              <p className="paywall-subscription-legal-links">
                <PaywallLegalLink
                  href={BILLING_LEGAL_URLS.privacyPolicy}
                  label={t.privacyPolicyLinkLabel}
                  disabled={busy}
                />
                <span aria-hidden="true"> · </span>
                <PaywallLegalLink
                  href={BILLING_LEGAL_URLS.appleStandardEula}
                  label={t.termsOfUseLinkLabel}
                  disabled={busy}
                />
              </p>
            </div>
          ) : null}
          {storeCatalogLoading ? (
            <p className="paywall-platform-note paywall-processing-note">{t.storeProductLoading}</p>
          ) : null}
          {storePurchaseProcessing ? (
            <p className="paywall-platform-note paywall-processing-note">{processingNote}</p>
          ) : null}
          {errorMessage && !storePurchaseProcessing ? <p className="paywall-error">{errorMessage}</p> : null}
          {showStoreFallbackLink && errorMessage && onOpenStoreProduct ? (
            <p className="paywall-platform-note">
              <button
                type="button"
                className="paywall-inline-link"
                disabled={busy}
                onClick={() => void onOpenStoreProduct()}
              >
                {t.storePurchaseOpenInStore}
              </button>
            </p>
          ) : null}
        </div>
        <div className="custom-prompt-footer paywall-footer">
          {isWebRedeem ? (
            <button
              type="button"
              className="custom-prompt-button ok"
              disabled={busy || !redeemCodeInput.trim()}
              onClick={() => onRedeem?.()}
              onTouchEnd={(e) =>
                !busy && redeemCodeInput.trim() && runTouchAction(e, () => onRedeem?.())
              }
            >
              {t.webRedeemActivate}
            </button>
          ) : showManage ? (
            <button
              type="button"
              className="custom-prompt-button ok"
              disabled={busy}
              onClick={() => onManage?.()}
              onTouchEnd={(e) => !busy && onManage && runTouchAction(e, onManage)}
            >
              {t.manage}
            </button>
          ) : (
            <button
              type="button"
              className="custom-prompt-button ok"
              disabled={busy || storeCatalogLoading}
              onClick={() => void onPurchase()}
              onTouchEnd={(e) =>
                !busy && !storeCatalogLoading && runTouchAction(e, () => void onPurchase())
              }
            >
              {hasStorePrice ? t.purchase(priceLabel) : t.purchaseFallback}
            </button>
          )}
          {showIssueButton && showManage ? (
            <button
              type="button"
              className="custom-prompt-button private"
              disabled={busy}
              onClick={() => onOpenWebRedeemIssue?.()}
              onTouchEnd={(e) =>
                !busy && onOpenWebRedeemIssue && runTouchAction(e, onOpenWebRedeemIssue)
              }
            >
              {t.webRedeemIssueOpenLink}
            </button>
          ) : null}
          {showRestore ? (
            <button
              type="button"
              className="custom-prompt-button private"
              disabled={busy}
              onClick={() => void onRestore()}
              onTouchEnd={(e) => !busy && runTouchAction(e, () => void onRestore())}
            >
              {t.restore}
            </button>
          ) : null}
          <button
            type="button"
            className="custom-prompt-button cancel"
            disabled={busy}
            onClick={onClose}
            onTouchEnd={(e) => !busy && runTouchAction(e, onClose)}
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
